/**
 * Auto-flip dropdown listboxes upward when they would overflow the bottom of
 * the viewport. macaw-ui's Combobox / DynamicCombobox renders the option list
 * with a hardcoded downward placement; when the trigger sits near the bottom
 * of the page (e.g. Tax class card in the product sidebar, Country area in
 * the warehouse form) the dropdown gets cut off and bottom options become
 * unreachable.
 *
 * The listbox is mounted with height: 0 and only expands after macaw runs its
 * own positioning, so a single rAF measurement is too early. We observe each
 * listbox via ResizeObserver and re-measure whenever its size changes, then
 * apply a CSS translateY to lift it above the trigger when needed.
 */

const FLIPPED_ATTR = "data-dropdown-flipped";
const VIEWPORT_PADDING = 16;
const TRIGGER_GAP = 56;

const tracked = new WeakSet<HTMLElement>();

function applyFlip(listbox: HTMLElement) {
  if (!listbox.isConnected) return;

  // Reset prior flip so we measure the natural downward position.
  const wasFlipped = listbox.hasAttribute(FLIPPED_ATTR);
  if (wasFlipped) {
    listbox.style.transform = "";
    listbox.removeAttribute(FLIPPED_ATTR);
  }

  const rect = listbox.getBoundingClientRect();
  if (rect.height === 0) return;

  const viewportHeight = window.innerHeight;
  const bottomOverflow = rect.bottom - (viewportHeight - VIEWPORT_PADDING);
  if (bottomOverflow <= 0) return;

  // Only flip if there's actually room above the listbox.
  const spaceAbove = rect.top;
  if (spaceAbove < rect.height + 8) return;

  const lift = rect.height + TRIGGER_GAP;
  listbox.style.transform = `translateY(-${lift}px)`;
  listbox.setAttribute(FLIPPED_ATTR, "true");
}

function track(listbox: HTMLElement) {
  if (tracked.has(listbox)) return;
  tracked.add(listbox);

  // ResizeObserver fires when macaw expands the listbox from height: 0 to its
  // real size, which is the moment our initial flip check missed before.
  const ro = new ResizeObserver(() => applyFlip(listbox));
  ro.observe(listbox);

  // When the listbox is removed from the DOM, stop observing.
  const mo = new MutationObserver(() => {
    if (!listbox.isConnected) {
      ro.disconnect();
      mo.disconnect();
    }
  });
  if (listbox.parentNode) {
    mo.observe(listbox.parentNode, { childList: true });
  }

  // Also measure now in case it already has dimensions on insert.
  applyFlip(listbox);
}

function isListbox(node: Node): node is HTMLElement {
  return (
    node instanceof HTMLElement && node.getAttribute("role") === "listbox"
  );
}

export function installDropdownAutoFlip() {
  if (typeof window === "undefined") return;

  const observer = new MutationObserver(mutations => {
    for (const m of mutations) {
      m.addedNodes.forEach(node => {
        if (isListbox(node)) {
          track(node);
        } else if (node instanceof HTMLElement) {
          node
            .querySelectorAll<HTMLElement>('[role="listbox"]')
            .forEach(track);
        }
      });
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });

  // Re-evaluate visible listboxes on window resize / scroll so a flipped
  // dropdown reverts when the user scrolls the trigger back into view.
  const reflowAll = () => {
    document
      .querySelectorAll<HTMLElement>('[role="listbox"]')
      .forEach(applyFlip);
  };
  window.addEventListener("resize", reflowAll, { passive: true });
  window.addEventListener("scroll", reflowAll, { passive: true, capture: true });

  // Catch any listboxes that already exist in the DOM at install time.
  document
    .querySelectorAll<HTMLElement>('[role="listbox"]')
    .forEach(track);
}
